import React from "react";
import {
  Dimensions,
  Pressable,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";

import NoBag from "@assets/svg/NoBag";
import PolyBagIcon from "@assets/svg/MyTasks/PolyBagIcon";
import Cash from "@assets/svg/RRD/Cash";
import Text from "@components/CustomText";
import Tag from "@components/Tag";
import colors from "@constants/color";
// eslint-disable-next-line import/no-named-as-default
import t from "@constants/localization";
import ArrowDown from "@assets/svg/ArrowDown";
import LinearGradient from "react-native-linear-gradient";
import Button from "./Button";
import ContactAndActionBtns from "./ContactAndActionBtns";
import DelayTag from "./DelayTag";
import DeliveredFooter from "./DeliveredFooter";
import RtoFooter from "./RtoFooter";
import RescheduleFooter from "./RescheduleFooter";
import TimeSlotTag from "./TimeSlotTag";

const DC_PALETTE = {
  escalatedBorder: "rgba(66, 116, 234, 1)",
  addressColor: "#4274EA",
  amountContainerBg: "rgba(236, 252, 229, 1)",
  addBagBg: "#F5F5F5",
  // Same indigo the leader's card uses for this chip, so one trip reads
  // identically on both apps. See tripLabel below.
  tripTagBg: "#EEF2FF",
  tripTagFg: "#4338CA",
};

// BI-17. Deliberately mirrors the leader app's own vocabulary rather than
// inventing a second one: tl-app
// src/screens/OutForDeliveryDetails/components/UserCard.js:18-31 (at ref
// 9f19421) renders `Trip · …{last 6}` once the id is longer than 10 chars,
// and the bare id otherwise.
//
// The boy's backend adds trip_id / trip_card_id ONLY to a card that has a
// trip, so the ABSENCE of trip_id is the pending signal and correctly yields
// no chip. We do NOT render the word "Pending": the leader reaches that label
// only via a `pending:` trip_card_id classifier, and the boy's backend
// deliberately emits no such classifier (emitting one would break flag-OFF
// byte-identity). No chip is therefore the honest render for a pending card.
export const tripLabel = (item) => {
  const tripId = item?.trip_id;
  if (!tripId) return null;
  const tid = String(tripId);
  return tid.length > 10 ? `Trip · …${tid.slice(-6)}` : `Trip · ${tid}`;
};

function DeliveryCard({
  item,
  onPressCall,
  openMaps,
  onPressBtn,
  handlePressItemspopup,
  handlePressEditCallOptions,
  taskActionConfig,
  contactOpts = [],
  addBagsHandler = () => {},
}) {
  const PATH = "screens/MyTask";
  const effectiveContactOpts =
    Array.isArray(item?.contact_btns) && item?.contact_btns.length > 0
      ? item?.contact_btns
      : Array.isArray(contactOpts) && contactOpts.length > 0
        ? contactOpts
        : ["DIALER"];
  const tripChip = tripLabel(item);
  return (
    <View
      style={[
        styles.listView,
        (item?.is_escalated || item?.is_priority_cx) &&
          styles.escalatedContainer,
      ]}
    >
      <LinearGradient
        colors={["#F8F8F8", "#EAEAEA"]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={styles.topCardHeader}
      >
        {/*
          The trip chip gets a row of its own, ABOVE the existing header row.
          It used to sit inside topLeftContainer beside route_name, where it
          added its whole width to a column already capped at 70% — the width
          the delay badge and the Earning box have to share what is left of.
          On its own row it costs the header row nothing, so that row lays out
          exactly as it did before the trip feature existed.

          A card with no trip renders NO row at all (tripLabel returns null when
          trip_id is absent, which is every card in production today), so the
          gradient band keeps a single child and is pixel-identical to today.
        */}
        {tripChip ? (
          <View style={styles.tripRow}>
            <Tag
              text={tripChip}
              bgcolor={DC_PALETTE.tripTagBg}
              textColor={DC_PALETTE.tripTagFg}
              borderColor={DC_PALETTE.tripTagFg}
            />
          </View>
        ) : null}
        <View style={styles.topCardHeaderRow}>
          <View style={styles.topLeftContainer}>
            <Text variant="body_text_3" style={styles.routeNameText}>
              {item?.route_name}
            </Text>
            {item?.show_items_list_popup ? (
              <Pressable
                onPress={() => handlePressItemspopup(item)}
                testID="mt-bm-btn"
              >
                <View style={styles.bagmodalContainer}>
                  <Text variant="subtitle_16" style={styles.viewListText}>
                    {t(`${PATH}/click_to_view_list`)}
                  </Text>
                  <ArrowDown />
                </View>
              </Pressable>
            ) : (
              <View style={styles.bagsContainer}>
                {Array.isArray(item?.bags) &&
                item?.bags?.length > 0 &&
                item?.state === "PACKED" &&
                item?.bags.every((bag) => bag !== "") ? (
                  item?.bags?.map((i, index) => (
                    <Text
                      key={`${i}-${index}`}
                      variant="heading_24"
                      style={styles.bagText}
                    >
                      {index === item?.bags?.length - 1 ? i : `${i}, `}
                    </Text>
                  ))
                ) : item?.show_no_bag_icon === true ? (
                  <View style={styles.noBagContainer}>
                    <NoBag />
                  </View>
                ) : null}
              </View>
            )}
            {item?.plastic_bag_opted_in && !item?.show_items_list_popup ? (
              <Pressable
                onPress={() => handlePressItemspopup(item)}
                testID="mt-bm-btn-plastic"
              >
                <View style={styles.bagmodalContainer}>
                  <Text variant="subtitle_16" style={styles.viewListText}>
                    {t(`${PATH}/click_to_view_list`)}
                  </Text>
                  <ArrowDown />
                </View>
              </Pressable>
            ) : null}
          </View>
          <View style={styles.topRightContainer}>
            <View style={styles.tagsContainer}>
              {item?.delay && <DelayTag delay={item?.delay} />}
              {item?.is_priority_cx && (
                <View style={styles.tagMarginTop}>
                  <Tag text="Priority" />
                </View>
              )}
              {item?.preferred_slots ? (
                <View style={styles.tagMarginTop}>
                  <TimeSlotTag timeSlot={item?.preferred_slots} />
                </View>
              ) : null}
            </View>
            <View style={styles.earningContainer}>
              <Text variant="body_text_3" style={styles.earningLabel}>
                {"Earning"}
              </Text>
              <Text variant="title_18_semi" style={styles.earningValue}>
                {item?.total_payout}
              </Text>
            </View>
          </View>
        </View>
      </LinearGradient>
      <View style={styles.cardHeaderContainer}>
        <View style={styles.view1}>
          <View style={styles.nameContainer}>
            <Text variant="body_text_2" style={styles.name}>
              {item?.user_name}
            </Text>
            <Text variant="caption_2" style={styles.distance}>
              {item?.distance}
            </Text>
            {item?.is_escalated && (
              <View style={styles.escalatedTagContainer}>
                <Tag text="Escalated" bgcolor="rgba(66, 116, 234, 1)" />
              </View>
            )}
          </View>
        </View>
        <TouchableOpacity
          testID="mt-open-map"
          onPress={() => openMaps(item?.cx_lat, item?.cx_lng)}
        >
          <Text variant="body_text_3" style={styles.address}>
            {item?.cx_formatted_address}
          </Text>
        </TouchableOpacity>
        {item?.phone_number_1 || item?.phone_number_2 ? (
          <View style={styles.dialerBtnContainer}>
            <ContactAndActionBtns
              item={item}
              onPressCall={onPressCall}
              handlePressEditCallOptions={handlePressEditCallOptions}
              taskActionConfig={taskActionConfig}
              contactOpts={effectiveContactOpts}
            />
            <View style={styles.dialerRightGroup}>
              {item?.plastic_bag_opted_in && (
                <View style={styles.bagsRequiredInline}>
                  <Tag
                    text="पॉली बैग"
                    bgcolor="#FFF9E6"
                    textColor="#B45309"
                    borderColor="#B45309"
                    icon={
                      <PolyBagIcon color="#B45309" width={14} height={14} />
                    }
                  />
                </View>
              )}
              {item?.show_add_bags_btn ? (
                <TouchableOpacity
                  onPress={() =>
                    addBagsHandler({
                      order_ids: item?.order_ids,
                      user_id: item?.user_id,
                      route_id: item?.route_id,
                      bags: item?.bags,
                      user_name: item?.user_name,
                      route_name: item?.route_name,
                      db_name: item?.db_name,
                    })
                  }
                  style={styles.addBagBtnContainer}
                >
                  <Text variant="body_text_4">
                    {item?.bags && item?.bags?.length
                      ? t(`${PATH}/changeBagNumber`) || "Change Bag Number"
                      : t(`${PATH}/addBagNumber`) || "Add Bag Number"}
                  </Text>
                </TouchableOpacity>
              ) : null}
            </View>
          </View>
        ) : null}
      </View>
      {item?.all_prepaid_orders ? (
        <View
          style={[
            styles.amountOverviewContainer,
            styles.amountSpaceBetween,
            item?.buttonsInfo?.length > 0 ? null : styles.amountMarginBottom,
          ]}
        >
          <View style={styles.cashRow}>
            <Cash pay={true} />
            <Text variant="body_text_1" style={styles.amountLabel}>
              {t(`${PATH}/prepaid_order`)}
            </Text>
          </View>
          <Text variant="body_text_1" style={styles.amountLabel}>
            {t(`${PATH}/pending_payment`)}
            <Text variant="body_text_2" style={styles.amountValue}>
              {item?.amount_to_collect_txt}
            </Text>
          </Text>
        </View>
      ) : Math.abs(item?.amount_to_collect) > 0 ? (
        <View
          style={[
            styles.amountOverviewContainer,
            item?.buttonsInfo?.length > 0 ? null : styles.amountMarginBottom,
          ]}
        >
          <Cash pay={true} />
          <Text variant="body_text_1" style={styles.amountLabel}>
            {t(`${PATH}/pay_by_cx`)}
            <Text variant="body_text_2" style={styles.amountValue}>
              {item?.amount_to_collect_txt}
            </Text>
          </Text>
        </View>
      ) : Math.abs(item?.amount_collected) > 0 ? (
        <View
          style={[
            styles.amountOverviewContainer,
            styles.amountPaddingHorizontal,
            item?.buttonsInfo?.length > 0 ? null : styles.amountMarginBottom,
          ]}
        >
          <Text variant="body_text_1" style={styles.amountLabel}>
            {t(`${PATH}/amount_collected`)}
            <Text variant="body_text_2" style={styles.amountValue}>
              {item?.amount_collected_txt}
            </Text>
          </Text>
        </View>
      ) : null}

      {item?.card_type === "RTO_CAN_CANCEL" && item?.footer && (
        <RtoFooter footer={item?.footer} />
      )}
      {item?.card_type === "DELIVERED" && item?.footer && (
        <>
          <View style={styles.divider} />
          <DeliveredFooter footer={item?.footer} />
        </>
      )}
      {item?.card_status === "RESCHEDULED" && item?.footer && (
        <>
          <View style={styles.divider} />
          <RescheduleFooter footer={item?.footer} />
        </>
      )}
      {item?.buttonsInfo?.length > 0 && (
        <View style={styles.bottomButtonContainer}>
          <View style={styles.buttonContainer}>
            {item?.buttonsInfo?.map((button) => (
              <Button
                key={button?.key}
                testID={`mt-button-${button?.key}`}
                title={button?.value}
                type={button?.type}
                backgroundColor={
                  button?.color === "primary"
                    ? "rgba(25, 129, 85, 1)"
                    : "rgba(0, 0, 0, 0.05)"
                }
                btnColor={
                  button?.color === "primary"
                    ? "rgba(25, 129, 85, 1)"
                    : "rgba(0, 0, 0, 0.2)"
                }
                textColor={
                  button?.color === "primary"
                    ? colors.white
                    : colors.mediumBlack
                }
                onPress={() => {
                  onPressBtn(button?.key, item);
                }}
                icon={button?.icon || null}
                disabled={button.color === "disabled"}
              >
                {item?.button_amount_txt ? (
                  <View style={styles.amountContainer}>
                    <Text variant="caption_3" style={styles.buttonAmountText}>
                      {item?.button_amount_txt}
                    </Text>
                  </View>
                ) : null}
                {item?.delivered_button_amount_txt ? (
                  <View style={styles.amountContainer}>
                    <Text variant="caption_3" style={styles.buttonAmountText}>
                      {item?.delivered_button_amount_txt}
                    </Text>
                  </View>
                ) : null}
              </Button>
            ))}
          </View>
        </View>
      )}
    </View>
  );
}

export default DeliveryCard;

const styles = StyleSheet.create({
  bottomButtonContainer: {
    marginBottom: 16,
    paddingHorizontal: 10,
  },
  buttonContainer: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
  },
  listView: {
    backgroundColor: colors.white,
    borderRadius: 10,
    elevation: 2,
    overflow: "hidden",
    marginHorizontal: 16,
    marginVertical: 8,
  },
  escalatedContainer: {
    borderWidth: 1,
    borderColor: DC_PALETTE.escalatedBorder,
  },
  view1: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  divider: {
    height: 1,
    backgroundColor: colors.lighterBlack,
  },

  name: {
    paddingHorizontal: 2,
    maxWidth: Dimensions.get("window").width * 0.45,
  },

  distance: {
    color: colors.mediumBlacklightBlack,
    marginLeft: 4,
    fontWeight: "500",
    lineHeight: 18,
  },
  address: {
    color: DC_PALETTE.addressColor,
    textDecorationLine: "underline",
  },
  cardHeaderContainer: {
    padding: 16,
    paddingVertical: 16,
    paddingTop: 12,
  },
  nameContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  amountOverviewContainer: {
    flexDirection: "row",
    paddingHorizontal: 16,
    alignItems: "center",
  },
  topCardHeader: {
    // A column now: an optional trip row, then the header row that used to BE
    // this element. With no trip chip there is one child and the band renders
    // exactly as the row did.
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    padding: 16,
    paddingVertical: 8,
    paddingBottom: 2,
    marginHorizontal: 1,
    marginTop: 1,
  },
  topCardHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    alignSelf: "stretch",
  },
  tripRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 4,
  },
  bagsContainer: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
  },
  bagmodalContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: colors.white,
    elevation: 1,
    marginVertical: 4,
    borderRadius: 14,
    justifyContent: "center",
    paddingVertical: 2,
    paddingHorizontal: 6,
  },
  dialerBtnContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },
  dialerRightGroup: {
    alignItems: "flex-end",
    flexShrink: 1,
  },
  bagsRequiredInline: {
    marginBottom: 8,
    flexShrink: 1,
  },
  amountContainer: {
    backgroundColor: DC_PALETTE.amountContainerBg,
    borderRadius: 9,
    justifyContent: "center",
    paddingBottom: 2,
    paddingTop: 1,
    paddingHorizontal: 6,
    marginLeft: 4,
    bottom: 1,
  },
  addBagBtnContainer: {
    marginTop: 12,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: DC_PALETTE.addBagBg,
    paddingHorizontal: 12,
    borderRadius: 8,
  },
  topLeftContainer: {
    maxWidth: "70%",
  },
  routeNameText: {
    color: colors.mediumBlacklightBlack,
  },
  viewListText: {
    marginRight: 6,
  },
  noBagContainer: {
    paddingBottom: 4,
  },
  bagText: {
    fontSize: 22,
    lineHeight: 28,
  },
  topRightContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  tagsContainer: {
    paddingBottom: 8,
  },
  tagMarginTop: {
    marginTop: 6,
  },
  earningContainer: {
    maxWidth: 120,
    alignItems: "flex-end",
    justifyContent: "flex-end",
    paddingLeft: 16,
  },
  earningLabel: {
    marginBottom: 2,
  },
  earningValue: {
    marginBottom: 4,
  },
  escalatedTagContainer: {
    marginLeft: 6,
    marginBottom: 2,
  },
  amountSpaceBetween: {
    justifyContent: "space-between",
  },
  amountMarginBottom: {
    marginBottom: 8,
  },
  amountPaddingHorizontal: {
    paddingHorizontal: 8,
  },
  cashRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  amountLabel: {
    paddingHorizontal: 8,
    lineHeight: 26,
    color: colors.mediumBlacklightBlack,
  },
  amountValue: {
    color: colors.black,
    lineHeight: 26,
  },
  buttonAmountText: {
    color: colors.btnGreen,
    fontWeight: "700",
  },
});
